import { createContext, useState, useMemo, useEffect } from 'react'
import { node } from 'prop-types'
import { api } from 'api'

const DiffContext = createContext({
  getDiffForCell: () => {
    return { changeType: '' }
  },
})

DiffContext.displayName = 'DiffContext'

export const DiffProvider = ({ children }) => {
  const [showChanges, setShowChanges] = useState(true)
  const [datasetId, setDatasetId] = useState(null)
  const [newDiffCommit, setNewDiffCommit] = useState(null)
  const [oldDiffCommit, setOldDiffCommit] = useState(null)
  const [loading, setLoading] = useState(false)
  const [diff, setDiff] = useState(null)

  useEffect(() => {
    const getDiff = async () => {
      setLoading(true)
      const newCommitRef = newDiffCommit.commit.uuid
      const oldCommitRef = oldDiffCommit && oldDiffCommit.commit.uuid
      const payload = { newCommitRef, oldCommitRef }
      const response = await api.post(`/api/v1/account/dataset/${datasetId}/data/diff`, payload)
      const diffResponse = response.data
      setDiff(diffResponse)
      setLoading(false)
      return diffResponse
    }

    if (!showChanges || !newDiffCommit) {
      setDiff(null)
      return
    }

    getDiff()
  }, [showChanges, datasetId, newDiffCommit, oldDiffCommit])

  const updateDiffCommitRefs = (datasetIdParam, newCommitParam, oldCommitParam) => {
    setDatasetId(datasetIdParam)
    setNewDiffCommit(newCommitParam)
    setOldDiffCommit(oldCommitParam)
  }

  const getDiffForCell = (cvId, fieldId) => {
    const rowDiff = diff && (diff.changes[cvId] || diff.additions[cvId] || diff.deletions[cvId])
    const cellDiff = rowDiff && rowDiff.changes[fieldId]
    return cellDiff
  }

  const updateShowChanges = showChangesParam => {
    setShowChanges(showChangesParam)
  }

  const value = useMemo(
    () => ({
      showChanges,
      datasetId,
      diff,
      newDiffCommit,
      oldDiffCommit,
      loading,
      updateShowChanges,
      updateDiffCommitRefs,
      getDiffForCell,
    }),
    [
      showChanges,
      datasetId,
      diff,
      loading,
      newDiffCommit,
      oldDiffCommit,
      updateShowChanges,
      updateDiffCommitRefs,
      getDiffForCell,
    ],
  )

  return <DiffContext.Provider value={value}>{children}</DiffContext.Provider>
}

DiffProvider.propTypes = {
  children: node.isRequired,
}
